import React, { useEffect } from 'react';
import { Input } from '@/components/ui/Form';
import useCredentialStore from '../useCredentialStore';
import { useFormContext } from 'react-hook-form';
import LtiConfiguration, { formWrapper, LtiStepProps } from './LtiConfiguration';
import LtiLaunchUrls from './LtiLaunchUrls';

const BRIGHTSPACE_HOST = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(?:[/?#].*)?$/i;

interface BrightspaceConfigurationProps extends LtiStepProps {
  rootUrl: string;
}

const BrightspaceConfiguration = ({
  subscriberId, next, previous, setLmsType, initialLmsType, rootUrl,
}: BrightspaceConfigurationProps) => {
  const ltiCredentials = useCredentialStore(state => state.ltiCredentials);

  const {
    formState: {
      errors,
    },
    register,
    watch,
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (initialLmsType === 'brightspace') {
      setValue('brightspace_url', ltiCredentials.lms_provider?.iss ?? '');
      setValue('client_id', ltiCredentials.client_id);
      setValue('deployment_id', ltiCredentials.deployment_id);
    }
  }, []);

  const brightspaceUrl = watch('brightspace_url');
  const deploymentId = watch('deployment_id');
  const clientId = watch('client_id');

  return (
    <LtiConfiguration
      disabled={!brightspaceUrl || !deploymentId || !clientId}
      initialLmsType={initialLmsType}
      lmsType="brightspace"
      next={next}
      previous={previous}
      setLmsType={setLmsType}
      subscriberId={subscriberId}
    >
      <LtiLaunchUrls rootUrl={rootUrl} />

      <div className="tw-mb-3">
        Then provide the credentials unique to your LTI 1.3 app installation. For instructions, see
        {' '}
        <a
          href="https://help.teachtci.com/s/article/Set-up-LTI-1-3-Grade-Sync-with-Brightspace-Admin"
          rel="noreferrer noopener"
          target="_blank"
        >
          How to set up LTI 1.3 with Brightspace
        </a>
      </div>

      <Input
        required
        errors={errors}
        label="Brightspace URL"
        placeholder="https://school.brightspace.com"
        variant="inline"
        {...register('brightspace_url', {
          required: 'Required',
          setValueAs: (value: string) => value.trim(),
          validate: (value: string) => (
            BRIGHTSPACE_HOST.test(value) || 'Enter your Brightspace URL, for example https://school.brightspace.com'
          ),
        })}
      />

      <Input
        required
        errors={errors}
        label="Client ID"
        placeholder="Enter Client ID"
        variant="inline"
        {...register('client_id', {
          required: 'Required',
          setValueAs: (value: string) => value.replace(/\s/g, ''),
        })}
      />

      <Input
        required
        errors={errors}
        label="Deployment ID"
        placeholder="Enter Deployment ID"
        variant="inline"
        {...register('deployment_id', {
          required: 'Required',
          setValueAs: (value: string) => value.replace(/\s/g, ''),
        })}
      />
    </LtiConfiguration>
  );
};

export default formWrapper(BrightspaceConfiguration);
